import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import { createWorkdayCase } from "../apis/apiWorkday";

/**
 * Creación del caso de Workday de una sanción (Flujo de atención → Resolución →
 * Sanciones): se le asigna al investigador responsable y trata sobre el
 * involucrado sancionado.
 *
 * Un caso creado es permanente y no se puede deshacer desde aquí, así que el
 * clic nunca escribe directo: primero se pregunta en una ventana de
 * confirmación y solo al aceptar se llama a djaguar, UNA vez, con
 * `confirm: true`. djaguar crea el caso y lo registra en la sanción en la misma
 * petición.
 *
 * El envío del denunciante (pestaña Información) usaba este mismo hook contra
 * el puente; djaguar registra los casos en la sanción, así que ese envío
 * necesitará su propio endpoint antes de volver a habilitarse.
 *
 * @param {Object} deps
 * @param {Function} deps.openModalConfirm  del hook useModalConfirm de la pantalla
 * @param {Function} deps.closeModalConfirm
 */
export const useWorkdayCase = ({ openModalConfirm, closeModalConfirm }) => {
  const [t] = useTranslation("report");
  const { enqueueSnackbar } = useSnackbar();

  // Envío en curso, por llave: una pantalla puede tener varios botones
  // (uno por involucrado sancionado) y solo debe bloquearse el que se oprimió.
  const [sending, setSending] = useState({});
  const isSending = (key) => !!sending[key];
  const mark = (key, value) => setSending((prev) => ({ ...prev, [key]: value }));

  /**
   * @param {Object} params
   * @param {string} params.key        identifica el botón que se oprimió
   * @param {string} params.reportId   id de la denuncia (report_identifier)
   * @param {number} params.sanctionId id de la sanción dentro de la denuncia
   * @param {string} params.soeid      forWorker: a quién se le asigna el caso
   * @param {string} [params.aboutSoeid] aboutWorker: de quién trata el caso
   * @param {string} params.title
   * @param {string} params.message
   * @param {string} [params.name] nombre de la persona a la que se le asigna
   * @param {string} [params.aboutName] nombre de la persona de la que trata
   * @param {Function} [params.onCreated] recibe la respuesta de djaguar cuando
   *   el caso ya existe en Workday; trae `workday`, el registro que quedó en la
   *   sanción (o no lo trae si djaguar no pudo guardarlo).
   * @param {Function} [params.onWorkday] recibe el `workday` que djaguar
   *   devuelve con un 409 o un 502 (el caso que ya tenía la sanción, la marca de
   *   envío sin confirmar o null si quedó libre), para que la pantalla lo pinte.
   */
  const sendToWorkday = ({
    key,
    reportId,
    sanctionId,
    soeid,
    aboutSoeid,
    title,
    message,
    name,
    aboutName,
    onCreated,
    onWorkday,
  }) => {
    if (!soeid || !reportId || sanctionId == null) return;

    const payload = { reportId, sanctionId, soeid, aboutSoeid, title, message };

    const submit = async ({ retry = false } = {}) => {
      mark(key, true);
      let created;
      try {
        created = await createWorkdayCase({ ...payload, confirm: true, retry });
      } catch (error) {
        console.error(error);
        const data = error?.response?.data;
        if (data && "workday" in data) onWorkday?.(data.workday);

        // El envío anterior quedó sin confirmar: solo se reenvía si el usuario
        // confirma que revisó en Workday y el caso no existe.
        if (error?.response?.status === 409 && data?.retry_allowed) {
          mark(key, false);
          askRetry(data.error);
          return;
        }
        const { message: text, variant } = describeWorkdayError(error, t);
        enqueueSnackbar(text, { variant, autoHideDuration: 12000 });
        mark(key, false);
        return;
      }

      // djaguar responde `caseID` (el folio "Caso-00219") y el WID va dentro de
      // `case`; el WID solo se muestra si faltara el folio.
      enqueueSnackbar(
        t("Resolution.workday_case_created", {
          case: created?.caseID || created?.case?.caseID || created?.case?.id || "",
        }),
        { variant: "success" }
      );
      // El caso se creó, pero no del todo como se pidió (p. ej. Workday
      // rechazó "Referido a" y el involucrado viaja solo en el mensaje).
      if (created?.warnings?.length) {
        enqueueSnackbar(
          `${t("Resolution.workday_case_warnings")} ${created.warnings.join(" · ")}`,
          { variant: "warning", autoHideDuration: 12000 }
        );
      }
      try {
        await onCreated?.(created);
      } catch (error) {
        console.error(error);
      } finally {
        mark(key, false);
      }
    };

    const askRetry = (reason) =>
      openModalConfirm({
        variant: "warning",
        title: t("Resolution.workday_retry_title"),
        description1: reason || "",
        description2: t("Resolution.workday_retry_description"),
        question: t("Care.do_you_want_continue"),
        leftButtonText: t("Care.cancel"),
        leftButtonColor: "secondary",
        leftButtonVariant: "outlined",
        onClickLeft: () => closeModalConfirm(),
        rightButtonText: t("Resolution.workday_retry_confirm"),
        rightButtonColor: "secondary",
        onClickRight: () => {
          closeModalConfirm();
          submit({ retry: true });
        },
      });

    // Se pregunta ANTES de llamar a djaguar: el caso se crea al aceptar.
    openModalConfirm({
      variant: "info",
      title: t("Resolution.workday_confirm_title"),
      description1: t("Resolution.workday_confirm_description", {
        name: name || "",
        soeid,
      }),
      // Cuando el caso trata sobre otra persona (sanciones: se le asigna al
      // investigador y trata sobre el involucrado) hay que decir de quién, o la
      // ventana solo muestra al investigador y se presta a confusión.
      description2: aboutSoeid
        ? `${t("Resolution.workday_confirm_about", {
            name: aboutName || "",
            soeid: aboutSoeid,
          })} ${t("Resolution.workday_confirm_permanent")}`
        : t("Resolution.workday_confirm_permanent"),
      question: t("Care.do_you_want_continue"),
      leftButtonText: t("Care.cancel"),
      leftButtonColor: "secondary",
      leftButtonVariant: "outlined",
      onClickLeft: () => closeModalConfirm(),
      rightButtonText: t("Resolution.workday_create_case"),
      rightButtonColor: "success",
      onClickRight: () => {
        closeModalConfirm();
        submit();
      },
    });
  };

  return { sendToWorkday, isSending };
};

/**
 * Aviso para un envío fallido. djaguar ya explica qué pasó (SOEID que no
 * existe, error de Workday, la sanción ya tiene caso, "revisa en Workday si el
 * caso existe"), así que su `error` se muestra tal cual.
 *
 * El timeout va aparte y como advertencia: el caso pudo crearse de todos modos.
 * Un reintento no lo duplica (djaguar dejó la sanción marcada y responde 409),
 * pero el usuario debe saber que hay que revisar.
 */
const describeWorkdayError = (error, t) => {
  if (error?.code === "ECONNABORTED" || error?.code === "ETIMEDOUT") {
    return { message: t("Resolution.workday_timeout"), variant: "warning" };
  }
  if (!error?.response) {
    return { message: t("Resolution.workday_unreachable"), variant: "error" };
  }
  const detail = error.response.data?.error;
  if (error.response.status === 409 && detail) {
    return { message: detail, variant: "warning" };
  }
  if (error.response.status === 403 && !detail) {
    return { message: t("Resolution.workday_forbidden"), variant: "error" };
  }
  return {
    message: detail
      ? `${t("Resolution.workday_error")} ${detail}`
      : t("Resolution.workday_error"),
    variant: "error",
  };
};

/**
 * Severidad de la denuncia para el caso de Workday.
 *
 * Nunca queda vacía: cuando no se ha definido (o sigue en el valor por defecto
 * `type::None`) se manda explícitamente como "No definida", para que el caso no
 * deje dudas de si es baja o simplemente no se evaluó.
 *
 * @param {Object} params
 * @param {Object} params.severity            reportDetail.severity ({ path, label, locales })
 * @param {Array}  params.severityOptions     opciones del contexto ({ value, label })
 * @param {Function} params.getLocalizedLabel de useLocalizedLabel
 * @param {string} params.undefinedLabel      texto cuando no está definida
 */
export const resolveSeverityLabel = ({
  severity,
  severityOptions = [],
  getLocalizedLabel,
  undefinedLabel,
}) => {
  const path = severity?.path;
  if (!path || String(path).toLowerCase() === "type::none") return undefinedLabel;

  return (
    severityOptions.find((option) => option.value === path)?.label ||
    getLocalizedLabel?.(severity) ||
    undefinedLabel
  );
};

/**
 * Cuerpo del caso: pares [etiqueta, valor], uno por renglón. Los valores vacíos
 * se omiten, así no salen renglones sueltos como "Área:".
 * @param {Array<[string, any]>} entries
 */
export const formatWorkdayMessage = (entries) =>
  entries
    .filter(([, value]) => value)
    .map(([label, value]) => `${label}: ${value}`)
    .join("\n");

export default useWorkdayCase;
